import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookmarkCheck, FolderKanban, Mail, MailCheck, ShieldCheck, Video } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageContainer } from '../../../common/components/PageContainer';
import { useTabParam } from '../../../common/hooks/use-tab-param.hook';
import {
    RoomProjectRow,
    RoomsFilter,
    SavedRoomRow,
    SavedRoomsList,
    SavedRoomsSettings,
    useSavedRoomsApi,
} from '../../../common/hooks/use-saved-rooms-api.hook';
import { ThumbStack } from '../../../components/design-system/analytics';
import {
    EmptyState,
    formatNumber,
    KpiCard,
    NUMERIC,
    PillTabs,
    RelativeTime,
    SearchField,
    SectionCard,
    StatusBadge,
} from '../../../components/design-system/admin';
import { PicturePlaceholder, SettingsGroup, SettingsToggleRow } from '../../../components/design-system/common';
import { ComebackPreviewDialog } from './components/ComebackPreviewDialog';
import { ProjectSheet, SavedRoomSheet } from './components/SavedRoomSheets';

const TABS = ['rooms', 'projects', 'settings'] as const;
type Tab = (typeof TABS)[number];
const FILTERS: RoomsFilter[] = ['all', 'shared', 'email', 'comeback', 'video', 'project'];
/** What the settings tab states about privacy and retention — the rules the API enforces (saved-rooms-privacy.ts). */
const PRIVACY_RULES = ['SHARING', 'RETENTION', 'DELETION', 'REQUESTS'] as const;

/**
 * Saved rooms: shoppers' rooms that come back.
 *
 * Who kept a room, what was in it, how big it is, whether they came back,
 * whether the comeback email reaches them and their video; the professionals'
 * projects with their quantities and board; and the vendor's switches.
 *
 * A saved room is the shopper's: its photo, render and video show here only
 * when they shared it with the shop. A private room shows what is in it —
 * products, area, quantities, visits — and a neutral frame where the picture
 * would be. The API holds the rest back; this page says so.
 */
export const SavedRoomsPage = () => {
    const { t } = useTranslation();
    const api = useSavedRoomsApi();
    const [tab, setTab] = useTabParam(TABS);
    const [filter, setFilter] = useState<RoomsFilter>('all');
    const [search, setSearch] = useState('');
    const [list, setList] = useState<SavedRoomsList | null>(null);
    const [projects, setProjects] = useState<RoomProjectRow[] | null>(null);
    const [error, setError] = useState(false);
    const [roomUuid, setRoomUuid] = useState<string | null>(null);
    const [project, setProject] = useState<RoomProjectRow | null>(null);
    const [preview, setPreview] = useState<{ open: boolean; roomUuid?: string }>({ open: false });

    const load = useCallback(async () => {
        setError(false);
        try {
            const [rooms, projs] = await Promise.all([api.rooms({ filter, search: search.trim() || undefined, limit: 200 }), api.projects()]);
            setList(rooms);
            setProjects(projs);
        } catch {
            setError(true);
        }
    }, [api, filter, search]);

    useEffect(() => {
        const timer = setTimeout(load, search ? 250 : 0);
        return () => clearTimeout(timer);
    }, [load, search]);

    const stats = list?.stats;
    const loading = !list && !error;

    return (
        <PageContainer breadcrumbs={[{ label: t('R2R.WORKSPACE.SALES') }, { label: t('R2R.NAV.ROOMS') }]}>
            <div className="mx-auto flex max-w-[1180px] flex-col gap-6" data-testid="saved-rooms-page">
                <div className="flex flex-col gap-1">
                    <h1 className="text-2xl font-medium tracking-tight">{t('SAVED_ROOMS.TITLE')}</h1>
                    <p className="text-sm text-muted-foreground">{t('SAVED_ROOMS.SUBTITLE')}</p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <KpiCard testId="saved-rooms-kpi-rooms" label={t('SAVED_ROOMS.KPI.ROOMS')} value={formatNumber(stats?.rooms)} hint={t('SAVED_ROOMS.KPI.ROOMS_SHARED_HINT', { count: stats?.shared ?? 0 })} icon={<BookmarkCheck />} loading={loading} />
                    <KpiCard label={t('SAVED_ROOMS.KPI.EMAIL')} value={formatNumber(stats?.withEmail)} hint={t('SAVED_ROOMS.KPI.EMAIL_HINT')} icon={<Mail />} loading={loading} />
                    <KpiCard label={t('SAVED_ROOMS.KPI.COMEBACK')} value={formatNumber(stats?.comebackSent)} hint={t('SAVED_ROOMS.KPI.COMEBACK_HINT', { count: stats?.comebackConsent ?? 0 })} icon={<MailCheck />} loading={loading} />
                    <KpiCard label={t('SAVED_ROOMS.KPI.PROJECTS')} value={formatNumber(stats?.projects)} hint={t('SAVED_ROOMS.KPI.PROJECTS_HINT')} icon={<FolderKanban />} loading={loading} />
                </div>

                <PillTabs<Tab>
                    value={tab}
                    onChange={setTab}
                    ariaLabel={t('SAVED_ROOMS.TITLE')}
                    options={[
                        { value: 'rooms', label: t('SAVED_ROOMS.TAB.ROOMS'), count: stats?.rooms },
                        { value: 'projects', label: t('SAVED_ROOMS.TAB.PROJECTS'), count: projects?.length },
                        { value: 'settings', label: t('SAVED_ROOMS.TAB.SETTINGS') },
                    ]}
                />

                {error && (
                    <Card className="p-6 text-sm text-muted-foreground">
                        {t('SAVED_ROOMS.ERROR')}{' '}
                        <Button variant="outline" size="sm" onClick={load} className="ms-2">
                            {t('SAVED_ROOMS.RETRY')}
                        </Button>
                    </Card>
                )}

                {tab === 'rooms' && !error && (
                    <SectionCard title={t('SAVED_ROOMS.LIST_TITLE')} description={t('SAVED_ROOMS.LIST_DESC')} flush testId="saved-rooms-table">
                        <div className="flex flex-col gap-3 px-4 pb-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
                            <PillTabs<RoomsFilter>
                                value={filter}
                                onChange={setFilter}
                                ariaLabel={t('SAVED_ROOMS.FILTER')}
                                options={FILTERS.map((f) => ({ value: f, label: t(`SAVED_ROOMS.FILTERS.${f.toUpperCase()}`) }))}
                            />
                            <SearchField value={search} onChange={setSearch} placeholder={t('SAVED_ROOMS.SEARCH')} />
                        </div>
                        <div className="border-t">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="min-w-[260px] ps-4 sm:ps-6">{t('SAVED_ROOMS.COL.ROOM')}</TableHead>
                                        <TableHead>{t('SAVED_ROOMS.COL.PRODUCTS')}</TableHead>
                                        <TableHead className="text-end">{t('SAVED_ROOMS.COL.AREA')}</TableHead>
                                        <TableHead>{t('SAVED_ROOMS.COL.LAST_VISIT')}</TableHead>
                                        <TableHead>{t('SAVED_ROOMS.COL.COMEBACK')}</TableHead>
                                        <TableHead className="pe-4 sm:pe-6">{t('SAVED_ROOMS.COL.VIDEO')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading &&
                                        [0, 1, 2, 3].map((i) => (
                                            <TableRow key={i}>
                                                <TableCell colSpan={6} className="ps-4 sm:ps-6">
                                                    <Skeleton className="h-10 w-full" />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    {list?.rows.map((row) => <RoomRow key={row.uuid} row={row} onOpen={() => setRoomUuid(row.uuid)} />)}
                                    {list && list.rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <EmptyState icon={<BookmarkCheck />} title={t('SAVED_ROOMS.EMPTY_TITLE')} description={t('SAVED_ROOMS.EMPTY_DESC')} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </SectionCard>
                )}

                {tab === 'projects' && !error && (
                    <SectionCard title={t('SAVED_ROOMS.PROJECTS_TITLE')} description={t('SAVED_ROOMS.PROJECTS_DESC')} flush testId="saved-rooms-projects">
                        <div className="border-t">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="min-w-[260px] ps-4 sm:ps-6">{t('SAVED_ROOMS.COL.PROJECT')}</TableHead>
                                        <TableHead className="text-end">{t('SAVED_ROOMS.COL.ROOMS')}</TableHead>
                                        <TableHead className="text-end">{t('SAVED_ROOMS.COL.AREA')}</TableHead>
                                        <TableHead>{t('SAVED_ROOMS.COL.WHO')}</TableHead>
                                        <TableHead className="pe-4 sm:pe-6">{t('SAVED_ROOMS.COL.LAST_VISIT')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {projects?.map((p) => (
                                        <TableRow key={p.uuid} className="cursor-pointer" onClick={() => setProject(p)} data-testid="saved-rooms-project-row">
                                            <TableCell className="ps-4 sm:ps-6">
                                                <div className="flex min-w-0 items-center gap-3">
                                                    {p.thumbnails.length ? (
                                                        <ThumbStack items={p.thumbnails.map((src) => ({ key: src, src }))} max={3} size="sm" />
                                                    ) : (
                                                        <PicturePlaceholder size="xs" label={t('SAVED_ROOMS.PRIVATE_ROOMS')} />
                                                    )}
                                                    <div className="min-w-0">
                                                        <p className="truncate text-sm font-medium">{p.name}</p>
                                                        <p className="truncate text-xs text-muted-foreground">{p.clientName ? t('SAVED_ROOMS.FOR_CLIENT', { client: p.clientName }) : '—'}</p>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className={`text-end ${NUMERIC}`}>{formatNumber(p.rooms.length)}</TableCell>
                                            <TableCell className={`text-end ${NUMERIC}`}>{p.areaM2 ? t('SAVED_ROOMS.ABOUT', { value: `${formatNumber(p.areaM2, 1)} m²` }) : '—'}</TableCell>
                                            <TableCell className="text-sm">{p.email || (p.hasEmail ? t('SAVED_ROOMS.EMAIL_PRIVATE') : t('SAVED_ROOMS.ANONYMOUS'))}</TableCell>
                                            <TableCell className="pe-4 text-muted-foreground sm:pe-6">
                                                <RelativeTime value={p.lastVisitAt} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {projects && projects.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <EmptyState icon={<FolderKanban />} title={t('SAVED_ROOMS.PROJECTS_EMPTY_TITLE')} description={t('SAVED_ROOMS.PROJECTS_EMPTY_DESC')} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </SectionCard>
                )}

                {tab === 'settings' && <SavedRoomsSettingsPanel onPreview={() => setPreview({ open: true })} />}
            </div>

            <SavedRoomSheet uuid={roomUuid} onClose={() => setRoomUuid(null)} onPreview={(uuid) => setPreview({ open: true, roomUuid: uuid })} />
            <ProjectSheet project={project} onClose={() => setProject(null)} />
            <ComebackPreviewDialog open={preview.open} roomUuid={preview.roomUuid} onClose={() => setPreview({ open: false })} />
        </PageContainer>
    );
};

function RoomRow({ row, onOpen }: { row: SavedRoomRow; onOpen: () => void }) {
    const { t } = useTranslation();
    const products = useMemo(() => {
        const seen = new Map<string, { key: string; src?: string; alt?: string }>();
        row.products.forEach((p) => !seen.has(p.productUuid) && seen.set(p.productUuid, { key: p.productUuid, src: p.thumbnail, alt: p.name }));
        return Array.from(seen.values());
    }, [row.products]);
    const cb = row.comeback;
    return (
        <TableRow className="cursor-pointer" onClick={onOpen} data-testid="saved-rooms-row">
            <TableCell className="ps-4 sm:ps-6">
                <div className="flex min-w-0 items-center gap-3">
                    {!row.shared ? (
                        <PicturePlaceholder size="xs" label={t('SAVED_ROOMS.PRIVATE_LONG')} testId="saved-rooms-row-private" />
                    ) : row.renderUrl || row.roomImageUrl ? (
                        <img src={(row.renderUrl || row.roomImageUrl)!} alt="" className="h-10 w-14 shrink-0 rounded-md border object-cover" />
                    ) : (
                        <PicturePlaceholder size="xs" reason="none" label={t('SAVED_ROOMS.NO_PICTURE')} />
                    )}
                    <div className="min-w-0">
                        <p className="flex min-w-0 items-center gap-2 text-sm font-medium">
                            <span className="truncate">{row.name}</span>
                            {row.shared ? (
                                <Badge variant="outline" className="shrink-0 font-normal" data-testid="saved-rooms-shared-badge">{t('SAVED_ROOMS.SHARED_WITH_YOU')}</Badge>
                            ) : (
                                <span className="shrink-0 text-xs font-normal text-muted-foreground">{t('SAVED_ROOMS.PRIVATE')}</span>
                            )}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                            {[row.leadName || row.email || (row.hasEmail ? t('SAVED_ROOMS.EMAIL_PRIVATE') : t('SAVED_ROOMS.ANONYMOUS')), row.project ? row.project.name : null, row.source === 'pro' ? t('SAVED_ROOMS.PRO') : null].filter(Boolean).join(' · ')}
                        </p>
                    </div>
                </div>
            </TableCell>
            <TableCell>
                <div className="flex items-center gap-2">
                    <ThumbStack items={products} max={4} size="xs" />
                    <span className={`text-xs text-muted-foreground ${NUMERIC}`}>{products.length}</span>
                </div>
            </TableCell>
            <TableCell className={`whitespace-nowrap text-end ${NUMERIC}`}>
                {row.areaM2 != null ? (row.areaApproximate ? t('SAVED_ROOMS.ABOUT', { value: `${formatNumber(row.areaM2, 1)} m²` }) : `${formatNumber(row.areaM2, 1)} m²`) : '—'}
            </TableCell>
            <TableCell className="whitespace-nowrap text-muted-foreground">
                <RelativeTime value={row.lastVisitAt} />
            </TableCell>
            <TableCell>
                {cb.unsubscribedAt ? (
                    <StatusBadge tone="neutral">{t('SAVED_ROOMS.COMEBACK.UNSUBSCRIBED')}</StatusBadge>
                ) : cb.consent ? (
                    <span className="text-sm">{t('SAVED_ROOMS.COMEBACK.SENT_COUNT', { count: cb.sentCount })}</span>
                ) : (
                    <span className="text-xs text-muted-foreground">{row.hasEmail ? t('SAVED_ROOMS.COMEBACK.NO_CONSENT') : '—'}</span>
                )}
            </TableCell>
            <TableCell className="pe-4 sm:pe-6">
                {row.video.status === 'ready' ? (
                    <span className="inline-flex items-center gap-1 text-sm">
                        <Video className="size-4" />
                        {t('SAVED_ROOMS.VIDEO.STATUS.READY')}
                    </span>
                ) : row.video.status === 'running' ? (
                    <span className="text-xs text-muted-foreground">{t('SAVED_ROOMS.VIDEO.STATUS.RUNNING')}</span>
                ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                )}
            </TableCell>
        </TableRow>
    );
}

/** The switches: saved rooms, the comeback email (with what it needs), the room video (with what it costs). */
function SavedRoomsSettingsPanel({ onPreview }: { onPreview: () => void }) {
    const { t } = useTranslation();
    const api = useSavedRoomsApi();
    const [settings, setSettings] = useState<SavedRoomsSettings | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.settings().then(setSettings).catch(() => undefined);
    }, [api]);

    const update = async (patch: Partial<Pick<SavedRoomsSettings, 'enabled' | 'comebackEmail' | 'roomVideo'>>) => {
        if (!settings) return;
        setSettings({ ...settings, ...patch });
        setSaving(true);
        try {
            setSettings(await api.updateSettings(patch));
        } finally {
            setSaving(false);
        }
    };

    if (!settings) return <Skeleton className="h-72 w-full" />;
    const comebackReady = settings.smtpConfigured && settings.postalAddress;

    return (
        <Card className="p-6" data-testid="saved-rooms-settings">
            <SettingsGroup title={t('SAVED_ROOMS.SETTINGS.TITLE')} description={t('SAVED_ROOMS.SETTINGS.DESC')}>
                <SettingsToggleRow
                    id="saved-rooms-enabled"
                    label={t('SAVED_ROOMS.SETTINGS.ENABLED')}
                    description={t('SAVED_ROOMS.SETTINGS.ENABLED_DESC')}
                    checked={settings.enabled}
                    disabled={saving}
                    onCheckedChange={(v: boolean) => update({ enabled: v })}
                />
                <SettingsToggleRow
                    id="saved-rooms-comeback"
                    label={t('SAVED_ROOMS.SETTINGS.COMEBACK')}
                    description={t('SAVED_ROOMS.SETTINGS.COMEBACK_DESC')}
                    checked={settings.comebackEmail}
                    disabled={saving || !settings.enabled}
                    onCheckedChange={(v: boolean) => update({ comebackEmail: v })}
                >
                    <div className="flex flex-col gap-2 pt-1">
                        {!comebackReady && (
                            <p className="text-xs text-muted-foreground" data-testid="saved-rooms-comeback-readiness">
                                {!settings.smtpConfigured ? t('SAVED_ROOMS.SETTINGS.NEEDS_SMTP') : t('SAVED_ROOMS.SETTINGS.NEEDS_ADDRESS')}
                            </p>
                        )}
                        <div>
                            <Button variant="outline" size="sm" onClick={onPreview} data-testid="saved-rooms-settings-preview">
                                <Mail />
                                {t('SAVED_ROOMS.COMEBACK.PREVIEW')}
                            </Button>
                        </div>
                    </div>
                </SettingsToggleRow>
                <SettingsToggleRow
                    id="saved-rooms-video"
                    label={t('SAVED_ROOMS.SETTINGS.VIDEO')}
                    description={t('SAVED_ROOMS.SETTINGS.VIDEO_DESC')}
                    checked={settings.roomVideo}
                    disabled={saving || !settings.enabled}
                    onCheckedChange={(v: boolean) => update({ roomVideo: v })}
                >
                    <p className="pt-1 text-xs text-muted-foreground" data-testid="saved-rooms-video-cost">
                        {t('SAVED_ROOMS.SETTINGS.VIDEO_COST', { credits: settings.videoCredits, usd: settings.videoCostUsd.toFixed(2) })}
                    </p>
                </SettingsToggleRow>
            </SettingsGroup>
            <div className="mt-6 border-t pt-6" data-testid="saved-rooms-privacy">
                <SettingsGroup title={t('SAVED_ROOMS.PRIVACY.TITLE')} description={t('SAVED_ROOMS.PRIVACY.DESC')}>
                    <ul className="flex flex-col gap-3 text-sm">
                        {PRIVACY_RULES.map((key) => (
                            <li key={key} className="flex items-start gap-2.5">
                                <ShieldCheck className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                                <span>{t(`SAVED_ROOMS.PRIVACY.${key}`)}</span>
                            </li>
                        ))}
                    </ul>
                </SettingsGroup>
            </div>
        </Card>
    );
}

export default SavedRoomsPage;
